import { defineComponent as _defineComponent } from 'vue'
import FiltersMenu from '../common/FiltersMenu.vue'
import TableFieldFilter from '../common/TableFieldFilter.vue'
import { useFilteredJobStoreLegacyAdapter as useJobFilterStore } from '../jobs/job-search-page/filteredJobStore'
import { computed, onMounted } from 'vue'
import { useGetJobFilterOptions } from '@/api/filter-options'
import { Dictionary, groupBy, orderBy, sortBy } from 'lodash'
import { useScheduleFilterStore } from './ScheduleFilterStore'
import { until } from '@vueuse/core'


export default /*#__PURE__*/_defineComponent({
  __name: 'ScheduleFiltersMenu',
  setup(__props) {

const scheduleViewStore = useScheduleFilterStore()
const store = useJobFilterStore()
const { data: filterOptions, isFetched: isFetchedFilterOptions } =
  useGetJobFilterOptions()

const divisionOptions = computed(() => {
  const grouped = groupBy(
    filterOptions.value?.jobFilterOptions.divisionOptions,
    (d) => d.hubName
  )

  return withHeaders(grouped)
})

const resourceTypeOptions = computed(() => {
  const grouped = groupBy(
    filterOptions.value?.jobFilterOptions.resourceTypeOptions,
    (d) => d.serviceName
  )

  return withHeaders(grouped)
})

const serviceProviderOptions = computed(() => {
  return [
    { id: null, name: 'No service provider' },
    ...(filterOptions?.value?.jobFilterOptions.serviceProviderOptions ?? []),
  ]
})

const withHeaders = function <T>(grouped: Dictionary<T[]>) {
  return orderBy(Object.keys(grouped))
    .map((groupKey) => [{ header: groupKey }, ...sortBy(grouped[groupKey])])
    .reduce((aggregate, items) => [...aggregate, ...items], [])
}

const setSelectedDivisions = ({
  ids,
  hidden,
}: {
  ids: string[]
  hidden: boolean
}) => {
  const options = filterOptions.value?.jobFilterOptions.divisionOptions
  const selectedDivisionsIds = options?.filter((d) => ids.includes(d.id))
  const selectedDivisions = selectedDivisionsIds?.map((d) => {
    return {
      label: d.name,
      value: d.id,
    }
  })
  store.$patch(
    {
      selectedDivisions,
    },
    hidden
  )
}

const setSelectedResourceTypes = ({
  ids,
  hidden,
}: {
  ids: number[]
  hidden: boolean
}) => {
  const options = filterOptions.value?.jobFilterOptions.resourceTypeOptions
  const selectedResourceTypesIds = options?.filter((rt) => ids.includes(rt.id))
  const selectedResourceTypes = selectedResourceTypesIds?.map((rt) => {
    return {
      label: rt.name,
      value: rt.id,
    }
  })
  store.$patch(
    {
      selectedResourceTypes,
    },
    hidden
  )
}

const setSelectedServiceProviders = ({
  ids,
  hidden,
}: {
  ids: (string | null)[]
  hidden: boolean
}) => {
  const options = filterOptions.value?.jobFilterOptions.serviceProviderOptions
  const hasNull = ids.includes(null)
  // filter out null if it exists
  const filteredIds = hasNull ? ids.filter((id) => id !== null) : ids
  const selectedServiceProviders = options?.filter((sp) =>
    filteredIds.includes(sp.id)
  )
  const selectedProviders = selectedServiceProviders?.map((sp) => {
    return {
      label: sp.name,
      value: sp.id,
    }
  })

  if (hasNull) {
    selectedProviders?.push({
      label: 'Unassigned',
      value: 'null',
    })
  }
  store.$patch(
    {
      selectedServiceProviders: selectedProviders,
    },
    hidden
  )
}

onMounted(async () => {
  await until(isFetchedFilterOptions).toBe(true)

  /*
   * NOTE: Populate Filters (if empty) for the Schedule View.
   *
   * Required for the functioning of how jobs are queried in
   * the schedule view; leaving the schedule page will populate the
   * filters for the jobs page (if none were selected to begin with).
   *
   * > Background:
   * > ---------------------------------------------------------------
   * > Schedule view still relies on knowing every selected option id,
   * > and filtering locally. We've recently updated filters on the Map
   * > view and Jobs page to work differently, where everything is visible
   * > by default and filters are "only used to narrow down results".
   * >
   * > Unfortunately for now this means we need to detect if all filters
   * > are unselected, and use that to "fake" everything being selected
   */
  if (store.selectedDivisions.value.length === 0) {
    setSelectedDivisions({
      ids:
        filterOptions.value?.jobFilterOptions.divisionOptions.map(
          (d) => d.id
        ) || [],
      hidden: true,
    })
  }

  if (store.selectedResourceTypes.value.length === 0) {
    setSelectedResourceTypes({
      ids:
        filterOptions.value?.jobFilterOptions.resourceTypeOptions.map(
          (d) => d.id
        ) || [],
      hidden: true,
    })
  }

  if (store.selectedServiceProviders.value.length === 0) {
    // The "" string is the "IsEmpty" Option.
    setSelectedServiceProviders({
      ids: [
        null,
        ...(filterOptions.value?.jobFilterOptions.serviceProviderOptions.map(
          (d) => d.id
        ) || []),
      ],
      hidden: true,
    })
  }
})

const showOfflineUnits = computed({
  get() {
    return scheduleViewStore.showOfflineUnits
  },
  set(value) {
    scheduleViewStore.updateShowOfflineUnits(value!)
  },
})

const showStandbyUnits = computed({
  get() {
    return scheduleViewStore.showStandbyUnits
  },
  set(value) {
    scheduleViewStore.updateShowStandbyUnits(value!)
  },
})

const showUnitsWithNoAssignedJobs = computed({
  get() {
    return scheduleViewStore.showUnitsWithNoAssignedJobs
  },
  set(value) {
    scheduleViewStore.updateShowUnitsWithNoAssignedJobs(value!)
  },
})

return { __sfc: true,scheduleViewStore, store, filterOptions, isFetchedFilterOptions, divisionOptions, resourceTypeOptions, serviceProviderOptions, withHeaders, setSelectedDivisions, setSelectedResourceTypes, setSelectedServiceProviders, showOfflineUnits, showStandbyUnits, showUnitsWithNoAssignedJobs, FiltersMenu, TableFieldFilter }
}

})